import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from './client';
import type { User, ApiResponse } from '../types/user';

// DTOs
export interface UpdateProfileDto {
  firstName?: string;
  lastName?: string;
  email?: string;
  emailNotificationsEnabled?: boolean;
}

export interface ChangePasswordDto {
  currentPassword: string;
  newPassword: string;
}

// API functions
const updateProfile = async (data: UpdateProfileDto): Promise<ApiResponse<User>> => {
  const response = await apiClient.patch('/users/me', data);
  return response.data;
};

const changePassword = async (data: ChangePasswordDto): Promise<void> => {
  await apiClient.post('/users/change-password', data);
};

// React Query Hooks

export function useUpdateProfile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateProfile,
    onSuccess: () => {
      // Invalidate auth/me to refresh user data
      queryClient.invalidateQueries({ queryKey: ['auth', 'me'] });
    },
  });
}

export function useChangePassword() {
  return useMutation({
    mutationFn: changePassword,
  });
}

// Auth API functions for password reset
export const forgotPassword = async (email: string): Promise<void> => {
  await apiClient.post('/auth/forgot-password', { email });
};

export const resetPassword = async (token: string, newPassword: string): Promise<void> => {
  await apiClient.post('/auth/reset-password', { token, newPassword });
};

export function useForgotPassword() {
  return useMutation({
    mutationFn: forgotPassword,
  });
}

export function useResetPassword() {
  return useMutation({
    mutationFn: ({ token, newPassword }: { token: string; newPassword: string }) =>
      resetPassword(token, newPassword),
  });
}
